// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { ChipMultiSelect } from "../ChipMultiSelect"
import { Field } from "../Field"
import { FreeTextListOrNone } from "../FreeTextListOrNone"
import { Input } from "@/components/ui/input"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

const CONDITIONS = [
  "pregnancy",
  "breastfeeding",
  "type2_diabetes",
  "hypertension",
  "kidney_disease",
  "celiac",
  "eating_disorder_history",
  "other",
] as const

/**
 * Step 13 (wire) — mandatory health-condition declaration (ADR-013
 * Decision 3, T04a `Step13Schema`). Displayed visually right after
 * allergens (see components/onboarding/steps.ts VISUAL_STEP_ORDER) even
 * though its wire step number stays 13. Conditions never unlock
 * therapeutic diet planning — see the disclaimer copy below.
 */
export function Step13HealthConditions({ value, onChange }: Props) {
  const { t } = useI18n()
  const conditions = (value.conditions as string[] | undefined) ?? []
  const otherText = (value.otherText as string | null | undefined) ?? null
  const medications = (value.medications as string[] | undefined) ?? []
  const none = value.none === true

  const chipValue: string[] | "none" = none ? "none" : conditions

  function applyConditions(next: string[] | "none") {
    if (next === "none") {
      onChange({ conditions: [], otherText: null, medications, none: true })
    } else {
      onChange({
        conditions: next,
        otherText: next.includes("other") ? otherText : null,
        medications,
        none: false,
      })
    }
  }

  const items = CONDITIONS.map(c => ({
    value: c,
    label: t(`onboarding.step13.conditions.${c}`),
  }))

  return (
    <div className="flex flex-col gap-5">
      <ChipMultiSelect
        legend={t("onboarding.step13.title")}
        items={items}
        value={chipValue}
        onChange={applyConditions}
        noneLabel={t("onboarding.step13.noneLabel")}
      />

      {conditions.includes("other") && (
        <Field label={t("onboarding.step13.otherLabel")} htmlFor="ob-health-other">
          <Input
            id="ob-health-other"
            placeholder={t("onboarding.step13.otherPlaceholder")}
            value={otherText ?? ""}
            onChange={e =>
              onChange({ conditions, otherText: e.target.value, medications, none: false })
            }
          />
        </Field>
      )}

      {!none && (
        <FreeTextListOrNone
          inputId="ob-medications"
          label={t("onboarding.step13.medicationsLabel")}
          placeholder={t("onboarding.step13.medicationsPlaceholder")}
          noneLabel={t("onboarding.step13.noneLabel")}
          required={false}
          value={medications.length === 0 ? "none" : medications}
          onChange={next =>
            onChange({
              conditions,
              otherText,
              medications: next === "none" ? [] : next,
              none: false,
            })
          }
        />
      )}

      {!none && conditions.length > 0 && (
        <div className="rounded-bb-md border border-line bg-paper p-4 text-sm">
          <p className="font-semibold text-ink">{t("onboarding.step13.disclaimerTitle")}</p>
          <p className="mt-1 text-ink-2">{t("onboarding.step13.disclaimerBody")}</p>
          <p className="mt-2 font-medium text-ink">{t("onboarding.step13.consultDoctor")}</p>
        </div>
      )}
    </div>
  )
}
